import { Component } from '@theme/component';

/**
 * A snap rail that advances itself one item at a time and loops forward forever, for strips with
 * no arrows (the value props). Swipe still works; touching or hovering pauses it.
 *
 * Looping: the whole set is cloned once after the originals. When the rail settles on a clone, it
 * jumps back by exactly one set's width with no animation, so the travel is always forwards — the
 * same trick `oudh-slideshow` uses, but stepping by item width rather than by viewport width, so it
 * works with one or several items in view.
 *
 * When every item already fits (the desktop grid), nothing moves; clones are hidden there by CSS.
 *
 * @extends {Component<{ rail: HTMLElement, items: HTMLElement[] }>}
 */
class OudhAutorail extends Component {
  #count = 0;
  /** @type {ReturnType<typeof setInterval> | undefined} */
  #timer;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #settleTimer;

  connectedCallback() {
    super.connectedCallback();

    const { rail, items } = this.refs;
    this.#count = items?.length ?? 0;
    if (!rail || this.#count < 2) return;

    // Clones carry no `ref` (Horizon would add them to refs.items) and are inert and hidden from
    // assistive tech, so the strip is announced once.
    for (const item of items) {
      const clone = /** @type {HTMLElement} */ (item.cloneNode(true));
      clone.removeAttribute('ref');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('data-clone', '');
      clone.inert = true;
      rail.append(clone);
    }

    rail.addEventListener('scroll', this.#onScroll, { passive: true });
    this.addEventListener('pointerenter', this.#pause);
    this.addEventListener('pointerleave', this.#play);
    this.addEventListener('touchstart', this.#pause, { passive: true });
    this.addEventListener('touchend', this.#play, { passive: true });
    this.addEventListener('focusin', this.#pause);
    this.addEventListener('focusout', this.#play);

    this.#play();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.refs.rail?.removeEventListener('scroll', this.#onScroll);
    clearTimeout(this.#settleTimer);
    this.#pause();
  }

  /** Distance between two neighbouring items, gap included. */
  get #step() {
    const items = this.refs.items;
    if (!items || items.length < 2 || !items[0] || !items[1]) return 0;
    return items[1].offsetLeft - items[0].offsetLeft;
  }

  /** True when the originals already fit, so there is nothing to scroll to. */
  get #fits() {
    const rail = this.refs.rail;
    if (!rail) return true;
    return this.#step * this.#count <= rail.clientWidth + 1;
  }

  #play = () => {
    if (this.dataset.autoplay !== 'true' || this.#count < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#pause();
    this.#timer = setInterval(() => this.#advance(), Number(this.dataset.interval) || 4000);
  };

  #pause = () => {
    if (this.#timer) clearInterval(this.#timer);
    this.#timer = undefined;
  };

  #advance() {
    const rail = this.refs.rail;
    const step = this.#step;
    if (!rail || step <= 0 || this.#fits) return;

    this.#normalise();
    rail.scrollTo({ left: rail.scrollLeft + step, behavior: 'smooth' });
  }

  #onScroll = () => {
    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(() => this.#normalise(), 140);
  };

  /** Once past the originals, move back one full set, instantly — the view is identical. */
  #normalise() {
    const rail = this.refs.rail;
    const setWidth = this.#step * this.#count;
    if (!rail || setWidth <= 0) return;

    if (rail.scrollLeft >= setWidth - 1) {
      rail.scrollTo({ left: rail.scrollLeft - setWidth, behavior: 'instant' });
    }
  }
}

if (!customElements.get('oudh-autorail')) {
  customElements.define('oudh-autorail', OudhAutorail);
}
